import { useApprovalStore } from '../stores/useApprovalStore';

export interface SSECallbacks {
  onStageStarted?: (data: { stage_number: number; stage_name: string; total_stages: number }) => void;
  onStageCompleted?: (data: { stage_number: number; stage_name: string }) => void;
  onText?: (data: { content: string }) => void;
  onThinking?: (data: { content: string }) => void;
  onToolStart?: (data: { name: string; input?: Record<string, unknown> }) => void;
  onAnalysisRecovered?: (data: { iteration: number; stage_number: number }) => void;
  onToolResult?: (data: { name: string; result: string; is_error: boolean }) => void;
  onToolProgress?: (data: { name: string; message: string }) => void;
  onApprovalRequired?: (data: { call_id: string; name: string; input: Record<string, unknown> }) => void;
  onDone?: () => void;
  onError?: (data: { message: string }) => void;
}

function dispatchSSEEvent(eventType: string, data: string, callbacks: SSECallbacks, chatId: string) {
  if (!eventType || !data) return;

  try {
    const parsed = JSON.parse(data);
    switch (eventType) {
      case 'approval_required':
        // Cross-cutting: route to the global approval store (keyed by chatId,
        // which only the transport knows) so the app-level modal shows it
        // regardless of which view opened this stream. Also fire the optional
        // callback for any local handling.
        useApprovalStore.getState().request(chatId, {
          callId: parsed.call_id, name: parsed.name, input: parsed.input,
        });
        callbacks.onApprovalRequired?.(parsed);
        break;
      case 'stage_started':
        callbacks.onStageStarted?.(parsed);
        break;
      case 'stage_completed':
        callbacks.onStageCompleted?.(parsed);
        break;
      case 'text':
        callbacks.onText?.(parsed);
        break;
      case 'thinking':
        callbacks.onThinking?.(parsed);
        break;
      case 'tool_start':
        callbacks.onToolStart?.(parsed);
        break;
      case 'tool_result':
        callbacks.onToolResult?.(parsed);
        break;
      case 'tool_progress':
        callbacks.onToolProgress?.(parsed);
        break;
      case 'heartbeat':
        // Keep-alive signal — treat same as any event
        break;
      case 'analysis_recovered':
        callbacks.onAnalysisRecovered?.(parsed);
        break;
      case 'done':
        callbacks.onDone?.();
        break;
      case 'error':
        callbacks.onError?.(parsed);
        break;
    }
  } catch {
    // skip malformed JSON
  }
}

export function connectSSE(
  chatId: string,
  message: string,
  profile: string,
  callbacks: SSECallbacks,
  token?: string,
  onKeepalive?: () => void,
  context?: Record<string, string>,
): () => void {
  const controller = new AbortController();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  (async () => {
    try {
      const body = JSON.stringify({ chat_id: chatId, message, profile, ...(context ? { context } : {}) });
      const send = (bearer?: string) =>
        fetch(`${baseUrl}/api/chat`, {
          method: 'POST',
          headers: bearer ? { ...headers, Authorization: `Bearer ${bearer}` } : headers,
          body,
          signal: controller.signal,
        });

      let response = await send();

      // A 401 here is usually a token that rotated while the page sat on the
      // processing view, not a dead session — renew once and retry before
      // giving up (mirrors authedFetch in ./client).
      if (response.status === 401) {
        const { refreshAccessToken, handleUnauthorized } = await import('./client');
        const fresh = await refreshAccessToken();
        if (fresh) response = await send(fresh);
        if (response.status === 401) {
          await handleUnauthorized();
          callbacks.onError?.({ message: 'HTTP 401' });
          return;
        }
      }

      if (!response.ok || !response.body) {
        callbacks.onError?.({ message: `HTTP ${response.status}` });
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // SSE state persists across chunks
      let buffer = '';
      let currentEvent = '';
      let currentData = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Flush any pending event
          if (currentEvent && currentData) {
            dispatchSSEEvent(currentEvent, currentData, callbacks, chatId);
          }
          callbacks.onDone?.();
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete last line in buffer

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            // If we have a pending event, dispatch it first
            if (currentEvent && currentData) {
              dispatchSSEEvent(currentEvent, currentData, callbacks, chatId);
              currentData = '';
            }
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line.startsWith(': ') || line === ':') {
            // SSE comment (keepalive ping) — signal liveness
            onKeepalive?.();
          } else if (line === '') {
            // Empty line = end of event
            if (currentEvent && currentData) {
              dispatchSSEEvent(currentEvent, currentData, callbacks, chatId);
            }
            currentEvent = '';
            currentData = '';
          }
        }

        // Yield to the event loop between chunks so the UI thread
        // can process React renders and keep the page responsive.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== 'AbortError') {
        callbacks.onError?.({ message: err.message });
      }
    }
  })();

  return () => controller.abort();
}
